import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { CLIENT_UNIT_PRICES_LIST } from 'src/constants/EndpointUrls';
import * as CssClassNames from 'src/constants/CssClassNames';
import { Label, Section, TextField, SelectField, RadioField, CheckboxField } from 'src/jbc-front/components/Form';
import Button from 'jbc-front/components/Button';
import { getSelectOptions, getRadioOptions } from 'src/reducers/selectOptions';

import { FormErrors } from 'src/components';
import { CancelBtn } from 'src/buttons';
import selector, { getOptionLabel } from 'src/utils/Utils';
import { redirectTo, concatParamsToUrl } from 'src/utils/Http';
import { getUnitPriceFormValues } from 'src/reducers/clients/unitPrices';
import { useTranslation } from 'react-i18next';
import { useTutorialContext } from '../../GuideTour/TutorialProvider';
import { isTutorialStep2Running } from '../../../utils/Tutorial';

const unitPriceListUrl = payrollRulesGroupId => concatParamsToUrl(CLIENT_UNIT_PRICES_LIST, { payrollRulesGroupId });

const ClientUnitPriceForm = props => {
  const { getAllTutorial, currentUserEmail, setTutorialStep2 } = useTutorialContext();
  const { t } = useTranslation();
  const {
    isSubmitting,
    setSubmitting,
    submit,
    pristine,
    handleSubmit,
    invalid,
    errors,
    standardUnitTypes,
    standardUnitTypeDescriptions,
    clientAllowances,
    fractionRoundingConditionTypes,
    fractionRoundingMethodTypes,
    useAllowanceTypes,
    payrollRulesGroupId,
    payrollRulesGroup,
    selectedStandardUnitType,
    selectedFractionRoundingMethodType,
    isTutorialRunning
  } = props;

  const onClickCancel = () => {
    if (isTutorialRunning) {
      const { run, stepIndex, tourCompleted, type, params } = getAllTutorial(currentUserEmail).tutorialStep2;
      setTutorialStep2({ run, stepIndex: stepIndex + 1, tourCompleted, type, params });
    }
    redirectTo(unitPriceListUrl(payrollRulesGroupId));
  };
  return (
    <form className={classNames(CssClassNames.FORM, CssClassNames.CLIENT_FORM)} onSubmit={handleSubmit}>
      <FormErrors errors={errors} />

      <Section>
        <TextField required label={t('features.ClientUnitPrices.containers.ClientUnitPriceForm.Unit_price_name')} name="name" />

        {payrollRulesGroup.payrollType === 'monthly' && (
          <RadioField
            required
            label={t('features.ClientUnitPrices.containers.ClientUnitPriceForm.Reference_salary_data')}
            name="useAllowanceType"
            options={useAllowanceTypes}
          />
        )}
        <div id={'formula'}>
          <RadioField
            required
            label={t('features.ClientUnitPrices.containers.ClientUnitPriceForm.Base_hours_days_for_unit_price')}
            name="standardUnitType"
            options={standardUnitTypes}
          />
          <Label text={getOptionLabel(selectedStandardUnitType)(standardUnitTypeDescriptions)} />
        </div>
        <Label required text={t('features.ClientUnitPrices.containers.ClientUnitPriceForm.Target_Items')} />
        {clientAllowances.map((clientAllowance, index) => (
          <CheckboxField
            key={`client_allowance_${clientAllowance.id}`}
            name={`clientAllowanceId[${index}]`}
            label={clientAllowance.label}
            normalize={value => value && clientAllowance.id}
          />
        ))}
        <div id={'rounding-section'}>
          <Label
            required
            text={t('features.ClientUnitPrices.containers.ClientUnitPriceForm.Rounding')}
            note={
              <div>
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.Please_refer_to_the_following_to_select_the_rounding_method')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.first_decimal_point')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.first_decimal_round_off')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.first_decimal_round_up')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.first_decimal_truncation')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.second_decimal_point')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.second_decimal_round_off')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.second_decimal_round_up')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.second_decimal_truncation')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.third_decimal_point')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.third_decimal_round_off')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.third_decimal_round_up')}
                <br />
                {t('features.ClientUnitPrices.containers.ClientUnitPriceForm.roundingExample.third_decimal_truncation')}
              </div>
            }
          />
          <RadioField name="fractionRoundingMethodType" options={fractionRoundingMethodTypes} />
          {selectedFractionRoundingMethodType !== 'not_perform' && (
            <SelectField name="fractionRoundingConditionType" options={fractionRoundingConditionTypes} />
          )}
        </div>
      </Section>
      <div className="u-ta-c">
        <CancelBtn onClick={onClickCancel} className="u-mr20" />
        <Button
          primary
          text="保存"
          onClick={() => {
            setSubmitting();
            submit();
          }}
          disabled={invalid || isSubmitting || pristine}
        >
          {t('features.EmployeeBonus.components.ActionButtonSection.save')}
        </Button>
      </div>
    </form>
  );
};

const mapStateToProps = state => ({
  standardUnitTypes: getRadioOptions(state, 'standardUnitTypes'),
  standardUnitTypeDescriptions: getRadioOptions(state, 'standardUnitTypeDescriptions'),
  clientAllowances: getSelectOptions(state, 'clientAllowances'),
  fractionRoundingConditionTypes: getSelectOptions(state, 'clientUnitPriceFractionRoundingConditionTypes'),
  fractionRoundingMethodTypes: getSelectOptions(state, 'clientUnitPriceFractionRoundingMethodTypes'),
  useAllowanceTypes: getSelectOptions(state, 'clientUnitPriceUseAllowanceTypes'),
  payrollRulesGroupId: selector(getUnitPriceFormValues(state), 'clientPayrollRulesGroupId'),
  selectedStandardUnitType: selector(getUnitPriceFormValues(state), 'standardUnitType'),
  selectedFractionRoundingMethodType: selector(getUnitPriceFormValues(state), 'fractionRoundingMethodType'),
  isTutorialRunning: isTutorialStep2Running()
});
export default connect(mapStateToProps)(ClientUnitPriceForm);
